import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Clock, ArrowRight } from "lucide-react";
import { api, img } from "../lib/api";
import { pillarMeta } from "../lib/constants";
import { usePlayer } from "../context/PlayerContext";

export default function EpisodeSpotlight() {
  const [ep, setEp] = useState(null);
  const { track, isPlaying, playTrack } = usePlayer();

  useEffect(() => {
    api.get("/episodes").then((r) => setEp(r.data[0] || null)).catch(() => {});
  }, []);

  if (!ep) return null;

  const meta = pillarMeta(ep.pillar);
  const isCurrent = track && track.id === ep.id && isPlaying;

  return (
    <section id="spotlight" className="relative py-16 px-5 sm:px-8 bg-[#050B14] grain overflow-hidden" data-testid="episode-spotlight">
      <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-[#0066FF]/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center rounded-3xl border border-[#0066FF]/25 bg-[#0C1424] overflow-hidden blue-box-glow"
        >
          {/* Cover */}
          <div className="relative aspect-video lg:aspect-[16/11] overflow-hidden group">
            <img
              src={img(ep.cover_file_id, ep.cover_url)}
              alt={ep.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              data-testid="spotlight-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0C1424] via-transparent to-transparent lg:bg-gradient-to-r" />
            <button
              onClick={() => playTrack(ep)}
              data-testid="spotlight-play-button"
              aria-label={isCurrent ? "Pause latest episode" : "Play latest episode"}
              className="absolute inset-0 m-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#0066FF] hover:bg-[#2B82FF] transition-colors blue-box-glow"
            >
              {isCurrent ? (
                <Pause className="h-9 w-9 text-white fill-white" />
              ) : (
                <Play className="h-9 w-9 text-white fill-white ml-1" />
              )}
            </button>
          </div>

          {/* Details */}
          <div className="p-7 sm:p-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="flex items-center gap-2 font-mono2 text-xs tracking-[0.3em] uppercase text-[#FF6B00]">
                <span className="h-2 w-2 rounded-full bg-[#FF3B30] animate-pulse" /> Latest Episode
              </span>
              <span
                className="font-mono2 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full"
                style={{ background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55` }}
              >
                {meta.short}
              </span>
            </div>

            <h2 className="font-display text-3xl sm:text-4xl uppercase leading-[0.95]" data-testid="spotlight-title">
              {ep.title}
            </h2>
            {ep.guest && <div className="mt-2 text-sm text-[#0066FF] font-mono2">with {ep.guest}</div>}
            <p className="mt-4 text-[#CBD5E1] leading-relaxed line-clamp-3">{ep.description}</p>

            <div className="mt-5 flex items-center gap-1.5 text-xs text-[#94A3B8] font-mono2">
              <Clock className="h-3.5 w-3.5" /> {ep.duration || "—"}
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                onClick={() => playTrack(ep)}
                data-testid="spotlight-listen-button"
                className="flex items-center gap-2 rounded-full bg-[#0066FF] hover:bg-[#2B82FF] transition-colors px-7 py-3.5 font-cond font-bold uppercase tracking-wide text-white blue-box-glow"
              >
                {isCurrent ? <Pause className="h-5 w-5 fill-white" /> : <Play className="h-5 w-5 fill-white" />}
                {isCurrent ? "Playing" : "Play Latest"}
              </button>
              <a
                href="#episodes"
                data-testid="spotlight-browse-link"
                className="flex items-center gap-2 rounded-full border border-[#0066FF]/40 text-[#94A3B8] hover:text-white hover:border-[#0066FF] transition-colors px-6 py-3.5 font-cond font-bold uppercase tracking-wide text-sm"
              >
                All Episodes <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
